import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './NotificationBell.css';

const TYPE_CONFIG = {
  note_added:           { icon: '📝', label: 'added a note to' },
  session_scheduled:    { icon: '🗓', label: 'scheduled a session with' },
  transcription_ready:  { icon: '✦', label: 'Transcription ready for' },
  client_shared:        { icon: '👥', label: 'shared' },
  member_joined:        { icon: '🎉', label: 'joined your Circle' },
  ownership_transferred:{ icon: '🔑', label: 'transferred Circle ownership to you' },
  invite_accepted:      { icon: '✓',  label: 'accepted your invite' },
};

const formatTime = (date) => {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60)   return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const buildMessage = (n) => {
  const cfg = TYPE_CONFIG[n.type] || { icon: '🔔', label: '' };
  const actor = n.payload?.actorName || 'A teammate';
  const client = n.payload?.clientName;

  switch (n.type) {
    case 'note_added':
    case 'session_scheduled':
      return `${actor} ${cfg.label} ${client || 'a client'}`;
    case 'transcription_ready':
      return `${cfg.label} ${client || 'a client'}`;
    case 'client_shared':
      return `${actor} ${cfg.label} ${client || 'a client'} with the Circle`;
    case 'member_joined':
      return `${actor} ${cfg.label}`;
    case 'ownership_transferred':
      return cfg.label;
    case 'invite_accepted':
      return `${actor} ${cfg.label}`;
    default:
      return n.payload?.message || 'New notification';
  }
};

const buildLink = (n) => {
  if (n.payload?.clientId) return `/clientdashboard/${n.payload.clientId}`;
  if (n.circleId) return `/circles/${n.circleId}/manage`;
  return '/dashboard';
};

const NotificationBell = ({ circleId }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };

  const fetchNotifications = useCallback(async () => {
    if (!circleId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${circleId}/notifications?limit=15`,
        { headers: authHeaders }
      );
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (err) {
      console.error('[NotificationBell]', err);
    } finally {
      setLoading(false);
    }
  }, [circleId]);

  // Fetch on mount + poll every 60s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleOpen = async () => {
    setOpen(prev => !prev);
    // Mark all as read when opening
    if (!open && unreadCount > 0 && circleId) {
      try {
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/circles/${circleId}/notifications/read`,
          { method: 'PUT', headers: authHeaders }
        );
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      } catch (err) {
        console.error('[NotificationBell] mark read:', err);
      }
    }
  };

  if (!circleId) return null;

  return (
    <div className="notif-bell-wrapper">
      <button
        ref={bellRef}
        className={`notif-bell-btn ${open ? 'active' : ''}`}
        onClick={handleOpen}
        title="Notifications"
      >
        <span className="notif-bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className="notif-panel">
          <div className="notif-panel-header">
            <span className="notif-panel-title">Circle notifications</span>
            {notifications.length > 0 && (
              <button
                className="notif-clear-btn"
                onClick={() => {
                  setNotifications([]);
                  setUnreadCount(0);
                }}
              >
                Clear
              </button>
            )}
          </div>

          <div className="notif-list">
            {loading && notifications.length === 0 && (
              <div className="notif-empty">Loading...</div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="notif-empty">
                <span className="notif-empty-icon">🔔</span>
                <p>No notifications yet</p>
                <span>Activity from your Circle will appear here</span>
              </div>
            )}

            {notifications.map((n) => {
              const cfg = TYPE_CONFIG[n.type] || { icon: '🔔' };
              return (
                <Link
                  key={n._id}
                  to={buildLink(n)}
                  className={`notif-item ${!n.read ? 'unread' : ''}`}
                  onClick={() => setOpen(false)}
                >
                  <div className="notif-item-icon">{cfg.icon}</div>
                  <div className="notif-item-content">
                    <p className="notif-item-msg">{buildMessage(n)}</p>
                    <span className="notif-item-time">{formatTime(n.createdAt)}</span>
                  </div>
                  {!n.read && <div className="notif-unread-dot" />}
                </Link>
              );
            })}
          </div>

          {notifications.length > 0 && (
            <Link
              to={`/circles/${circleId}/manage`}
              className="notif-panel-footer"
              onClick={() => setOpen(false)}
            >
              View Circle →
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
